import { isArray } from 'lodash';
import { getCsrfToken } from '../csrf';
import type { Interceptor } from '@connectrpc/connect';
import { retryFetch } from './retry';
import { canHttpRetry } from './retry-helpers';
import type { HttpMethod } from '../custom-types';

export function buildQuery(params: { [key: string]: unknown }): string {
  const queryParams = Object.keys(params)
    .filter((name) => params[name] !== undefined)
    .map((name) => {
      const value = params[name];
      if (isArray(value)) {
        return value.map((item) => `${name}=${encodeURIComponent(item)}`).join('&');
      }
      return `${name}=${encodeURIComponent(value as string | number | boolean)}`;
    });
  if (queryParams.length === 0) {
    return '';
  }
  return `?${queryParams.join('&')}`;
}

export function buildRequestInit(base: Partial<RequestInit>): RequestInit {
  const request = {
    credentials: 'include' as const,
    ...base,
  };

  if (!request.headers) {
    request.headers = {};
  }

  const token = getCsrfToken();

  if (token) {
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    request.headers['X-Csrf-Token'] = token;
  }

  const context = ContextService.getCurrentContext();

  if (context) {
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    request.headers['User-Context'] = context;
  }

  return request;
}

/**
 * Retry an auto-generated API fetch until it succeeds or maxTries is reached.
 *
 * @see {@link retryAsync} for more information and WARNINGs.
 */
export const retryGeneratedFetch = async (httpMethod: HttpMethod, fn: () => Promise<Response>): Promise<Response> => {
  const result = await retryFetch(fn, {
    shouldRetryOnResponse: (response) => {
      if (response.status === 404 && httpMethod.toUpperCase() === 'DELETE') {
        return false;
      }
      return canHttpRetry(response.status);
    },
  });
  return result.isOk()
    ? result.value
    : result.error.isValueError
      ? result.error.lastResponse
      : Promise.reject(result.error.lastFailure);
};

export async function extractError(response: Response): Promise<OperationResult<OperationError>> {
  try {
    const content = (await response.json()) as OperationError;
    return {
      content,
      status: response.status,
      headers: {},
    };
  } catch (error) {
    let message = 'Unknown error';
    if (response.status === 401) {
      // TODO(collin.irwin): why are we only setting the message for 401?! This makes these issues hard to debug in sentry.
      // Change it to always include the status text.
      message = response.statusText || 'Unauthorized';
    }
    return {
      status: response.status,
      headers: {},
      content: {
        message,
        originalError: error,
      },
    };
  }
}

export interface OperationResult<Content> {
  readonly status: number;
  readonly headers: { readonly [key: string]: string };
  readonly content: Content;
}

export interface OperationError {
  readonly message: string;
  readonly originalError: unknown;
}

export class ContextService {
  private static currentContext: string | undefined;

  public static setCurrentContext(newContext: string | undefined) {
    this.currentContext = newContext;
  }

  public static getCurrentContext() {
    return this.currentContext;
  }
}

export const vennHeadersInterceptor: Interceptor = (next) => async (req) => {
  const token = getCsrfToken();

  if (token) {
    req.header.set('X-Csrf-Token', token);
  }

  const context = ContextService.getCurrentContext();

  if (context) {
    req.header.set('User-Context', context);
  }

  return next(req);
};
